import { defineStore } from "pinia"

export const useTicketsStore = defineStore("tickets", {
  state: () => ({
    tickets: [],
    selectedTicket: null,
    ticketMessages: {},
    isLoading: false,
    error: null,
  }),

  getters: {
    openTickets: (state) => state.tickets.filter((t) => ["open", "in_progress"].includes(t.status)),

    closedTickets: (state) => state.tickets.filter((t) => t.status === "closed"),

    ticketsByPriority: (state) => (priority) => {
      return state.tickets.filter((t) => t.priority === priority)
    },

    getTicketMessages: (state) => (ticketId) => {
      return state.ticketMessages[ticketId] || []
    },
  },

  actions: {
    /**
     * Fetch all tickets
     */
    async fetchTickets(filters = {}) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/shipment/support-tickets", { params: filters })

        this.tickets = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch tickets"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Get specific ticket
     */
    async getTicket(ticketId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipment/support-tickets/${ticketId}`)

        this.selectedTicket = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch ticket"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Create new ticket
     */
    async createTicket(ticketData) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/shipment/support-tickets", ticketData)

        this.tickets.unshift(response.data.data)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create ticket"
        return { success: false, error: this.error, errors: error.response?.data?.errors }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update ticket
     */
    async updateTicket(ticketId, updates) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipment/support-tickets/${ticketId}`, updates)

        const index = this.tickets.findIndex((t) => t.id === ticketId)
        if (index !== -1) {
          this.tickets[index] = response.data.data
        }

        if (this.selectedTicket?.id === ticketId) {
          this.selectedTicket = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update ticket"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Close ticket
     */
    async closeTicket(ticketId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/support-tickets/${ticketId}/close`)

        const index = this.tickets.findIndex((t) => t.id === ticketId)
        if (index !== -1) {
          this.tickets[index] = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to close ticket"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch ticket messages
     */
    async fetchTicketMessages(ticketId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipment/support-tickets/${ticketId}/messages`)

        this.ticketMessages[ticketId] = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch messages"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Add message to ticket
     */
    async addTicketMessage(ticketId, message, attachments = []) {
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/support-tickets/${ticketId}/messages`, {
          message,
          attachments,
        })

        if (!this.ticketMessages[ticketId]) {
          this.ticketMessages[ticketId] = []
        }
        this.ticketMessages[ticketId].push(response.data.data)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to send message"
        return { success: false, error: this.error }
      }
    },

    /**
     * Clear error
     */
    clearError() {
      this.error = null
    },

    /**
     * Reset store
     */
    reset() {
      this.tickets = []
      this.selectedTicket = null
      this.ticketMessages = {}
      this.isLoading = false
      this.error = null
    },
  },
})
